import { Component, HostBinding, Inject } from '@angular/core';

import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { environment as env_const } from '@env/environment';
import { TreeNodeModel } from '@common/models/id-name-model';
import { StoreService } from '@common/services/store.service';
import { ThemeService } from '@common/services/theme/theme.service';
import { TabBar } from '@components/tab-bar/interfaces/tab-bar';

export type FolderWorkspaceKey = 'private' | 'development';

export interface FolderSelectionWorkspaces {
  private: TreeNodeModel;
  development: TreeNodeModel;
  initial?: FolderWorkspaceKey;
}

export interface FolderSelectionDialogData {
  title?: string;
  titleKey?: string;
  folders: TreeNodeModel;
  selectedFolder?: TreeNodeModel;
  workspaces?: FolderSelectionWorkspaces;
}

// https://github.com/angular/material2/issues/3593
// https://stackoverflow.com/questions/42717508/how-to-communicate-from-angular-material2-dialog-to-its-parent

@Component({
  selector: 'tiwp-folder-selection-dialog',
  templateUrl: './folder-selection-dialog.component.html',
  styleUrls: ['./folder-selection-dialog.component.scss']
})
export class FolderSelectionDialogComponent {
  onDisabledTooltipText = 'disabled';
  tabs: TabBar[] = [];
  currentTabIndex = 0;
  activeWorkspace: FolderWorkspaceKey = 'private';
  displayedFolders: TreeNodeModel;
  selectedFolder: TreeNodeModel | undefined;

  @HostBinding('class.folder-selection-dialog--light-well')
  get lightWellTreeBackground(): boolean {
    return this.themeService.getCurrentTheme() === 'light-theme';
  }

  constructor(
    private translate: TranslateService,
    private themeService: ThemeService,
    private store: StoreService,
    public dialogRef: MatDialogRef<FolderSelectionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: FolderSelectionDialogData
  ) {
    this.displayedFolders = data.folders;
    this.selectedFolder = data.selectedFolder;
    if (data.workspaces) {
      this.tabs = this.buildWorkspaceTabs();
      const initial = data.workspaces.initial === 'development' ? 'development' : 'private';
      this.currentTabIndex = initial === 'development' ? 1 : 0;
      this.applyWorkspace(initial, data.selectedFolder);
    }
    this.translate.get('common.tooltip.disabled').subscribe(
      ( tooltipText: string ) => this.onDisabledTooltipText = tooltipText,
      ( error ) => {
        this.onDisabledTooltipText = 'disabled';
        console.error(
          '%cLanguage error: ', 'color:black;background:red', error.message, 'Key: common.tooltip.disabled'
        );
      }
    );
  }

  stopPropagation( event: Event ) {
    event.stopPropagation();
  }

  getExpandBreadcrumb( action: ( node: TreeNodeModel ) => void ) {
    if ( this.selectedFolder ) {
      action ( this.selectedFolder );
    }
  }

  onWorkspaceChange(tabIndex: number): void {
    this.currentTabIndex = tabIndex;
    const key: FolderWorkspaceKey = tabIndex === 1 ? 'development' : 'private';
    this.applyWorkspace(key, this.selectedFolder);
  }

  selectFolder( clickedFolder: TreeNodeModel ) {
    if ( clickedFolder ) {
      this.dialogRef.close( clickedFolder );
    }
  }

  private applyWorkspace(key: FolderWorkspaceKey, previous?: TreeNodeModel): void {
    this.activeWorkspace = key;
    const tree = this.data.workspaces?.[key] ?? this.data.folders;
    this.displayedFolders = tree;
    const previousId = previous?.id;
    this.selectedFolder = previousId != null
      ? (findFolderNode(tree, previousId) || tree)
      : tree;
  }

  private buildWorkspaceTabs(): TabBar[] {
    const userInfo = this.store.getUserInfo();
    const types = this.store.getAccessibilityTypes() ?? [];
    return [
      {
        id: env_const.accessibilityTypeIds.private,
        title: this.workspaceTitle('private', types),
        subtitle: userInfo?.username,
        icon: { name: 'privacy_tip' },
      },
      {
        id: env_const.accessibilityTypeIds.development,
        title: this.workspaceTitle('development', types),
        subtitle: userInfo?.organization?.short_name,
        icon: { name: 'developer_board' },
      },
    ];
  }

  private workspaceTitle(
    key: FolderWorkspaceKey,
    types: Array<{id: number; name: string; i18n_key?: string}>
  ): string {
    const typeId = env_const.accessibilityTypeIds[key];
    const named = types.find((t) => t.id === typeId || t.i18n_key === key);
    if (named?.name) {
      return named.name;
    }
    return this.translate.instant('accessibilityTypes.' + key);
  }
}

function findFolderNode(node: TreeNodeModel, id: number): TreeNodeModel | null {
  if (!node) {
    return null;
  }
  if (node.id === id) {
    return node;
  }
  for (const child of node.children || []) {
    const hit = findFolderNode(child, id);
    if (hit) {
      return hit;
    }
  }
  return null;
}
